import React from 'react';
import {Button, Paper, Typography} from '@mui/material';
import Grid from '@mui/material/Grid2';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import Constants from '../../../utils/constants.js';
import PiluleVerdict from '../PiluleVerdict.js';
import {BadgeFlux, BadgeQuantite, BadgeTypologie} from '../fiche/FicheBadges.js';
import {libelleCollecte, libelleDernierPassage, libelleEtat} from './carte.selectors.js';
import './CartePopup.css';

/**
 * Contenu de la popup d'un PAV (B1). Recopie de `app/components/map/ConteneurMarkerPopup` (même rendu : bandeau titre,
 * libellés en ligne, badges, vignette, barre d'actions) sans Redux ni entité conteneur API : tout arrive en props.
 * L'ancrage Leaflet est fourni par `PopupPav` (CarteLeaflet.js).
 * `derniereCollecte` (lot 8) : `undefined` = bouton Collecte à 0 (pas de ligne) ; événement COLLECTE / COLLECTE_IMPOSSIBLE ou `null` (« — »).
 * `cause` (lot 11, style Causes) : ligne déjà formatée (`libelleCause`) ou absente.
 * `photoApres` : chemin de la photo APRÈS du dernier passage, ou null. `interventionId` : null -> pas de fiche PDF.
 */
export default function CartePopup({pav, etat, photoApres, interventionId, derniereCollecte, cause, onOuvrirFiche, onPdf}) {
    const [photoKo, setPhotoKo] = React.useState(false);
    const [photoChargee, setPhotoChargee] = React.useState(false);
    React.useEffect(() => {
        setPhotoKo(false);
        setPhotoChargee(false);
    }, [photoApres]);
    // Place décidée à l'ouverture : avec photo APRÈS -> bloc de 260 px réservé (squelette puis image), sans -> aucun bloc.
    const adresse = pav.adresse && pav.adresse.trim() ? pav.adresse.trim() : '—';
    return (
        <Paper elevation={0} className="carte-popup-container" role="dialog" aria-label={`PAV ${pav.nomenclature}`}>
            <Grid container size={12}>
                <Grid size={12}>
                    <Typography variant="subtitle1" className="carte-popup-title">
                        {pav.nomenclature} : {adresse}, {pav.communeLibelle || '—'}
                    </Typography>
                </Grid>
                {photoApres && (
                    <Grid size={12} className="carte-popup-photo-cadre">
                        {!photoKo && (
                            <img
                                src={`${Constants.API_ASSETS_PHOTO_INTERVENTION}/${photoApres}`}
                                alt="Après lavage"
                                className="carte-popup-photo"
                                onLoad={() => setPhotoChargee(true)}
                                onError={() => setPhotoKo(true)}
                            />
                        )}
                        {!photoChargee && <div className={`carte-popup-squelette${photoKo ? ' carte-popup-squelette-fixe' : ''}`} aria-hidden="true"/>}
                    </Grid>
                )}
                <Grid container size={12} sx={{padding: '.5rem .75rem'}}>
                    <Grid size={12} className="carte-popup-badges">
                        <BadgeFlux flux={pav.flux}/>
                        <BadgeTypologie typologie={pav.typologie}/>
                        <BadgeQuantite quantite={pav.quantite}/>
                        <PiluleVerdict verdict={etat.verdict} libelle={libelleEtat(etat)} taille={18}/>
                    </Grid>
                    <Grid size={12} sx={{marginTop: '.5rem'}}>
                        <Typography className="carte-popup-label">Dernier passage : {libelleDernierPassage(etat)}</Typography>
                        {derniereCollecte !== undefined && (
                            <Typography className="carte-popup-label">Dernière collecte : {libelleCollecte(derniereCollecte)}</Typography>
                        )}
                        {cause && <Typography className="carte-popup-label">{cause}</Typography>}
                    </Grid>
                </Grid>
                <Grid container size={12} className="carte-popup-actions" justifyContent="space-evenly">
                    <Grid>
                        <Button color="primary" size="small" variant="contained" onClick={() => onOuvrirFiche(pav.id)}>
                            Ouvrir la fiche
                        </Button>
                    </Grid>
                    <Grid>
                        <Button
                            color="primary"
                            size="small"
                            variant="contained"
                            disabled={interventionId == null}
                            onClick={() => onPdf(interventionId)}
                            startIcon={<PictureAsPdfIcon/>}
                        >
                            Fiche PDF
                        </Button>
                    </Grid>
                </Grid>
            </Grid>
        </Paper>
    );
}

/** Photo APRÈS et identifiant d'intervention du dernier passage (`dernierPassage` de fiche.selectors), ou nulls. */
export function contenuPassage(passage) {
    if (!passage) return {photoApres: null, interventionId: null};
    let photoApres = null;
    for (const e of passage.evenements) {
        const p = (e.photos || []).find((ph) => ph.etiquette === 'APRES');
        if (p) {
            photoApres = p.url;
            break;
        }
    }
    return {photoApres, interventionId: passage.interventionId};
}
